import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { appSettings } from '../../../environments/app-settings';
import { disableErrorHandler } from '../../http/error.interceptor';
import { DataLake } from '../../models/data-lake';
import { EntityAction } from '../../models/entity-action';
import { Fileshare } from '../../models/fileshare';
import { PageResult } from '../../models/page-result';
import { Storage } from '../../models/storage';
import { TableConfiguration } from '../../models/table-configuration';
import { File as RequestFile } from '../models/files';
import { Request } from '../models/request';
import { RequestVersion } from '../models/request-version';
import { WorkItem } from '../models/work-item';

/**
 * Encapsulates data utilization request related operations.
 */
@Injectable()
export class RequestService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${appSettings.api.url}ui/requests`;

  /**
   * Retrieves page of data utilization requests.
   *
   * @param sortBy Name of the field for ordering.
   * @param isDescending Flag which displays if ordering should be descending.
   * @param searchText Search text.
   * @param filters Array of statuses to display.
   * @param continuationToken Next page token.
   * @returns A page of data utilization requests.
   */
  public getPage(sortBy: string, isDescending: boolean, searchText: string, filters: string[], continuationToken: string): Observable<PageResult<WorkItem>> {
    let params = new HttpParams()
      .set('sortBy', sortBy)
      .set('isDescending', isDescending.toString());

    if (searchText)
      params = params.set('searchText', searchText);

    for (const filter of filters)
      params = params.append('filter', filter);

    let headers = new HttpHeaders();
    if (continuationToken)
      headers = headers.set('x-syk-continuation', continuationToken);

    return this.http.get<PageResult<WorkItem>>(this.baseUrl, { params, headers });
  }

  /**
   * Retrieves blank data utilization request with allowed actions list.
   *
   * @returns Blank data utilization request.
   */
  public blank(): Observable<EntityAction<Request>> {
    return this.http.get<EntityAction<Request>>(`${this.baseUrl}/blank`);
  }

  /**
   * Retrieves existing data utilization request with allowed actions list.
   *
   * @param id Data utilization request ID.
   * @param creatorId Data utilization request creator user ID.
   * @returns Data utilization request.
   */
  public get(id: string, creatorId: string): Observable<EntityAction<Request>> {
    return this.http.get<EntityAction<Request>>(`${this.baseUrl}/${id}/users/${creatorId}`);
  }

  /**
   * Retrieves existing data utilization request version with allowed actions list.
   *
   * @param id Data utilization request ID.
   * @param creatorId Data utilization request creator user ID.
   * @param version Data utilization request version number.
   * @returns Data utilization request version.
   */
  public getVersion(id: string, creatorId: string, version: number): Observable<EntityAction<RequestVersion>> {
    return this.http.get<EntityAction<RequestVersion>>(`${this.baseUrl}/${id}/versions/${version}/users/${creatorId}`);
  }

  /**
   * Adds new data utilization request.
   *
   * @param version Data utilization request to add.
   * @returns Actual data utilization request.
   */
  public create(version: RequestVersion): Observable<EntityAction<Request>> {
    return this.http.post<EntityAction<Request>>(this.baseUrl, version);
  }

  /**
   * Updates existing data utilization request version.
   *
   * @param request Data utilization request.
   * @param version Data utilization request version to update.
   * @returns Actual data utilization request version.
   */
  public update(request: Request, version: RequestVersion): Observable<EntityAction<RequestVersion>> {
    return this.http.put<EntityAction<RequestVersion>>(`${this.baseUrl}/${request.id}/versions/${version.version}/users/${request.creatorID}`, version);
  }

  /**
   * Uploads data utilization request file.
   *
   * @param request Data utilization request.
   * @param version Data utilization request version.
   * @param file Data utilization request file.
   * @returns Actual data utilization request file.
   */
  public addFile(request: Request, version: RequestVersion, file: any): Observable<RequestFile> {
    const form = new FormData();
    form.append('file', file);

    return this.http.post<RequestFile>(`${this.baseUrl}/${request.id}/versions/${version.version}/users/${request.creatorID}/files`, form);
  }

  /**
   * Deletes data utilization request file.
   *
   * @param request Data utilization request.
   * @param version Data utilization request version.
   * @param file Data utilization request file to delete.
   * @returns Empty (no content) response.
   */
  public deleteFile(request: Request, version: RequestVersion, file: RequestFile): Observable<any> {
    return this.http.delete(`${this.baseUrl}/${request.id}/versions/${version.version}/users/${request.creatorID}/files/${file.id}`);
  }

  /**
   * Downloads data utilization request file content.
   *
   * @param request Data utilization request.
   * @param version Data utilization request version.
   * @param file Data utilization request file.
   * @returns Data utilization request file content.
   */
  public downloadFile(request: Request, version: RequestVersion, file: RequestFile): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/${request.id}/versions/${version.version}/users/${request.creatorID}/files/${file.id}/download`, { responseType: 'blob', observe: 'response' }).pipe(
      map(response => {
        const blob = new Blob([response.body], { type: response.headers.get('Content-Type') });
        return new File([blob], file.name, { type: blob.type });
      }));
  }

  /**
   * Submits (sets pending) data utilization request version.
   *
   * @param request Data utilization request.
   * @param version Data utilization request version to update.
   * @returns Actual data utilization request version.
   */
  public submit(request: Request, version: RequestVersion): Observable<EntityAction<RequestVersion>> {
    return this.http.put<EntityAction<RequestVersion>>(`${this.baseUrl}/${request.id}/versions/${version.version}/users/${request.creatorID}/submit`, null);
  }

  /**
   * Approves data utilization request version.
   *
   * @param request Data utilization request.
   * @param version Data utilization request version to update.
   * @returns Actual data utilization request version.
   */
  public approve(request: Request, version: RequestVersion): Observable<EntityAction<RequestVersion>> {
    return this.http.put<EntityAction<RequestVersion>>(`${this.baseUrl}/${request.id}/versions/${version.version}/users/${request.creatorID}/approve`, null);
  }

  /**
   * Rejects data utilization request version.
   *
   * @param request Data utilization request.
   * @param version Data utilization request version to update.
   * @param reason Rejection reason.
   * @returns Actual data utilization request version.
   */
  public reject(request: Request, version: RequestVersion, reason: string): Observable<EntityAction<RequestVersion>> {
    const params = new HttpParams()
      .set('reason', reason);

    return this.http.put<EntityAction<RequestVersion>>(`${this.baseUrl}/${request.id}/versions/${version.version}/users/${request.creatorID}/reject`, null, { params });
  }

  /**
   * Cancels data utilization request version.
   *
   * @param request Data utilization request.
   * @param version Data utilization request version to update.
   * @param reason Cancellation reason.
   * @returns Actual data utilization request version.
   */
  public cancel(request: Request, version: RequestVersion, reason: string): Observable<EntityAction<RequestVersion>> {
    const params = new HttpParams()
      .set('reason', reason);

    return this.http.delete<EntityAction<RequestVersion>>(`${this.baseUrl}/${request.id}/versions/${version.version}/users/${request.creatorID}`, { params });
  }

  /**
   * Creates updated data utilization request version.
   *
   * @param request Data utilization request.
   * @param version Data utilization request version to update.
   * @param reason Update reason.
   * @returns Updated data utilization request version.
   */
  public setUpdating(request: Request, version: RequestVersion, reason: string): Observable<EntityAction<RequestVersion>> {
    const params = new HttpParams()
      .set('reason', reason);

    return this.http.post<EntityAction<RequestVersion>>(`${this.baseUrl}/${request.id}/versions/${version.version}/users/${request.creatorID}`, null, { params });
  }

  /**
   * Accepts data utilization request version terms of use.
   *
   * @param request Data utilization request.
   * @param version Data utilization request version to update.
   * @returns Actual data utilization request version.
   */
  public acceptTermsOfUse(request: Request, version: RequestVersion): Observable<EntityAction<RequestVersion>> {
    return this.http.put<EntityAction<RequestVersion>>(`${this.baseUrl}/${request.id}/versions/${version.version}/users/${request.creatorID}/toc/accept`, null);
  }

  /**
   * Retrieves data utilization request file shares.
   *
   * @param id Data utilization request ID.
   * @param creatorId Data utilization request creator user ID.
   * @returns Data utilization request file shares.
   */
  public getFileshares(id: string, creatorId: string): Observable<Storage<Fileshare>> {
    return this.http.get<Storage<Fileshare>>(`${this.baseUrl}/${id}/users/${creatorId}/fileshares`, { context: disableErrorHandler() });
  }

  /**
   * Retrieves data utilization request ADLS Gen 2 directories.
   *
   * @param id Data utilization request ID.
   * @param creatorId Data utilization request creator user ID.
   * @returns Data utilization request ADLS Gen 2 directories.
   */
  public getDataLakes(id: string, creatorId: string): Observable<Storage<DataLake>> {
    return this.http.get<Storage<DataLake>>(`${this.baseUrl}/${id}/users/${creatorId}/adls`, { context: disableErrorHandler() });
  }

  /**
   * Retrieves configuration data for request table with allowed actions list.
   *
   * @returns EntityAction instance with TableConfiguration.
   */
  public getConfiguration(): Observable<EntityAction<TableConfiguration>> {
    return this.http.get<EntityAction<TableConfiguration>>(`${this.baseUrl}/configuration`);
  }
}
